// @flow
import * as React from 'react';
import { useEffect } from 'react';
import Grid from 'styled-components-grid';
import { connect } from 'react-redux';
import { Router, withTranslation, nextRoutes } from '@i18n';
import { NextSeo } from 'next-seo';

import compose from 'lodash/fp/compose';
import { BLOG_CATEGORY, YAPITA_HEALTH_URL } from '@routes';

import { LoadingSpinner, MetaRobots } from '@components';
import waitForPageReady from '@lib/waitForPageReady';

import ArticlesList from '../components/ArticlesList';
import CategoryList from '../components/CategoryList';
import { requestBlogArticles, requestBlogCategories } from '../redux/actions';

import type { Props } from './types';

import {
  MainSubHeadline,
  StyledArticleCategoryListContainer,
  StyledSpinnerContainer,
  StyledcategoryListGridUnit
} from '../styles';

import { DisplayCount, StyledContentContainer, StyledTopHeadingWrapper } from './styles';

const BlogCategory = ({
  query,
  blogCategories,
  requestBlogCategories,
  blogArticles,
  totalPages,
  t
}: Props): React.Node => {
  const pageNumber = parseInt(query.pageNumber || 1, 10);
  const slug: string = query?.slug || '';
  const { locale } = nextRoutes;
  const { id } = query;
  const loading = false;

  const setPage = (pageNumber: number): void => {
    Router.pushRoute(BLOG_CATEGORY, { pageNumber, slug, id });
  };

  const CategoryTitle = (blogCategories || []).find(category => category.id === id)?.attributes
    .title;

  const generateUrl = () => {
    const baseUrl = `${YAPITA_HEALTH_URL}/${locale}/blog/categories/${slug}/${id}`;
    return baseUrl;
  };

  useEffect(() => {
    requestBlogCategories();
  }, []);

  if (loading || !blogArticles) {
    return (
      <StyledSpinnerContainer>
        <LoadingSpinner />
      </StyledSpinnerContainer>
    );
  }

  const metaData: { MetaTitle: string, MetaDescription: string } = t('Seo:BlogCategoryListing', {
    returnObjects: true,
    category: CategoryTitle
  });

  return (
    <>
      <NextSeo
        title={metaData.MetaTitle}
        description={metaData.MetaDescription}
        canonical={generateUrl()}
        openGraph={{
          url: generateUrl(),
          title: metaData.MetaTitle,
          description: metaData.MetaDescription,
          site_name: 'Yapita Health',
          images: [
            {
              url:
                'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />
      <MetaRobots index follow />

      <StyledContentContainer>
        <Grid>
          <Grid.Unit size={{ mobile: 12 / 12, desktop: 8 / 12 }}>
            <StyledTopHeadingWrapper>
              <MainSubHeadline>{CategoryTitle || t('AllPosts')}</MainSubHeadline>

              <DisplayCount>{t('Count', { count: blogArticles.length })}</DisplayCount>
            </StyledTopHeadingWrapper>
            <StyledcategoryListGridUnit visible={{ mobile: true, desktop: false }}>
              <StyledArticleCategoryListContainer>
                <CategoryList categories={blogCategories || []} activeCategory={id} t={t} />
              </StyledArticleCategoryListContainer>
            </StyledcategoryListGridUnit>

            <ArticlesList
              articles={blogArticles || []}
              categories={blogCategories || []}
              numberOfPages={totalPages}
              currentPage={pageNumber}
              setPage={setPage}
              pathname="/blog/category"
            />
          </Grid.Unit>

          <StyledcategoryListGridUnit visible={{ mobile: false, desktop: true }} size={4 / 12}>
            <StyledArticleCategoryListContainer>
              <CategoryList categories={blogCategories || []} activeCategory={id} t={t} />
            </StyledArticleCategoryListContainer>
          </StyledcategoryListGridUnit>
        </Grid>
      </StyledContentContainer>
    </>
  );
};

const mapStateToProps = ({ blogData }) => ({
  blogArticles: blogData.data,
  blogCategories: blogData.categories,
  totalPages: blogData.noOfPages
});

BlogCategory.getInitialProps = async ({ query, store }) => {
  store.dispatch(
    requestBlogArticles({
      categoryTypes: [query.id],
      pageNumber: query.pageNumber
    })
  );
  await waitForPageReady(store);
  return { query };
};

export default compose(
  connect(
    mapStateToProps,
    {
      requestBlogArticles,
      requestBlogCategories
    }
  ),
  withTranslation('Blog')
)(BlogCategory);
